import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { CONTROL_PANEL_TRANSLATIONS } from '../i18n/controlPanel';
import { EXTRA_TRANSLATIONS } from '../i18n/translations';
import { getLocaleFromLang } from '../utils/localizedContent';

const baseTranslations = {
  es: {
    nav: { home: 'Inicio', store: 'Tienda', reservations: 'Reservas', events: 'Eventos', tournaments: 'Torneos', ranking: 'Ranking', collections: 'Colecciones', profile: 'Perfil', admin: 'Panel de control', login: 'Iniciar Sesión', register: 'Registrarse', logout: 'Cerrar Sesión', cart: 'Carrito', cards: 'Cartas' },
    home: {
      hero_title: 'Tu Rincón de Juegos', hero_subtitle: 'Tienda, torneos y reservas para amantes de los juegos de mesa y TCG en un solo lugar.',
      hero_cta: 'Ver Tienda', hero_cta2: 'Reservar Mesa', featured: 'Productos Destacados', events_title: 'Próximos Eventos',
      see_all: 'Ver Todos', add_to_cart: 'Añadir', join_title: '¡Únete a la Comunidad!',
      join_sub: 'Regístrate y accede a reservas, torneos, colecciones y mucho más.',
      join_cta: 'Crear Cuenta', feature_store: 'Tienda Online', feature_store_desc: 'Juegos de mesa y TCG a los mejores precios.',
      feature_reserve: 'Reserva de Juegos', feature_reserve_desc: 'Prueba nuestros juegos en la tienda antes de comprar.',
      feature_table: 'Reserva de Mesas', feature_table_desc: 'Ven con tus propios juegos y reserva una mesa.',
      feature_events: 'Torneos', feature_events_desc: 'Compite en torneos de Pokémon, Magic y más.',
    },
    store: {
      title: 'Tienda', all: 'Todo', board_games: 'Juegos de Mesa', tcg: 'TCG', accessories: 'Accesorios',
      search_placeholder: 'Buscar productos...', price_filter: 'Precio', sort: 'Ordenar',
      add_to_cart: 'Añadir al Carrito', rent: 'Reservar', out_of_stock: 'Sin stock',
      players: 'jugadores', minutes: 'min', no_products: 'No se encontraron productos'
    },
    reservations: {
      game_title: 'Reserva de Juegos de Mesa', table_title: 'Reserva de Mesas',
      select_game: 'Selecciona un Juego', select_date: 'Selecciona Fecha', select_slot: 'Selecciona Horario',
      available: 'Disponible', booked: 'Ocupado', confirm: 'Confirmar Reserva',
      my_reservations: 'Mis Reservas', cancel: 'Cancelar', cancelled: 'Cancelada', confirmed: 'Confirmada',
      duration: 'Duración', party_size: 'Personas', notes: 'Notas', table: 'Mesa', live: 'En vivo', offline: 'Desconectado',
    },
    events: {
      title: 'Eventos', tournaments_title: 'Torneos', register: 'Inscribirse', unregister: 'Cancelar Inscripción',
      registered: 'Inscrito', free: 'Gratis', participants: 'participantes', full: 'Completo',
      prize: 'Premio', entry_fee: 'Inscripción', date: 'Fecha', time: 'Hora',
      tournament: 'Torneo', casual: 'Casual', draft: 'Draft', sealed: 'Sellado',
      events_subtitle: 'Eventos, noches de juego y actividades.',
      tournaments_subtitle: 'Torneos, brackets y competiciones.',
      empty_events: 'No hay eventos próximos.',
      empty_tournaments: 'No hay torneos próximos.',
    },
    collections: {
      title: 'Colecciones de Cartas', my_collections: 'Mis Colecciones', public_collections: 'Colecciones Públicas',
      create: 'Nueva Colección', add_card: 'Añadir Carta', search_cards: 'Buscar Cartas',
      pokemon: 'Pokémon', magic: 'Magic', private: 'Privada', public: 'Pública',
      cards: 'cartas', edit: 'Editar', delete: 'Eliminar', save: 'Guardar', cancel: 'Cancelar',
      share: 'Compartir', copy_link: 'Copiar Enlace', link_copied: '¡Enlace copiado!'
    },
    wishlist: {
      title: 'Lista de Deseos', add: 'Añadir a Deseos', remove: 'Quitar de Deseos', empty: 'Tu lista de deseos está vacía',
      added: '¡Añadido a la lista!', removed: 'Eliminado de la lista', view: 'Ver Lista de Deseos'
    },
    product_reserve: {
      title: 'Reservar Producto', reserve: 'Reservar', cancel: 'Cancelar Reserva',
      reserved: 'Reservado', out_of_stock: 'Agotado - Reservar', preorder: 'Pre-pedido',
      my_reservations: 'Mis Reservas de Productos', status_pending: 'Pendiente', status_confirmed: 'Confirmado',
      success: '¡Producto reservado! Te avisaremos cuando esté disponible.',
      people_reserved: 'personas han reservado este producto'
    },
    cart: {
      title: 'Carrito', empty: 'Tu carrito está vacío', checkout: 'Pagar con Stripe',
      total: 'Total', remove: 'Eliminar', continue_shopping: 'Seguir Comprando',
      login_required: 'Inicia sesión para comprar'
    },
    auth: {
      login: 'Iniciar Sesión', register: 'Registrarse', email: 'Correo electrónico',
      password: 'Contraseña', name: 'Nombre', google_login: 'Continuar con Google',
      or: 'o', forgot_password: '¿Olvidaste tu contraseña?', no_account: '¿No tienes cuenta?', has_account: '¿Ya tienes cuenta?'
    },
    admin: {
      dashboard: 'Panel de Control', users: 'Usuarios', products: 'Productos', events: 'Eventos',
      reservations: 'Reservas', revenue: 'Ingresos', new_product: 'Nuevo Producto', new_event: 'Nuevo Evento',
    },
    profile: { title: 'Mi Perfil', edit: 'Editar Perfil', orders: 'Mis Pedidos', reservations: 'Mis Reservas', save: 'Guardar', cancel: 'Cancelar' },
    loyalty: {
      title: 'Puntos de Fidelidad', points: 'puntos', your_points: 'Tus Puntos', earn_points: 'Gana Puntos',
      redeem: 'Canjear', redeem_points: 'Canjear Puntos', history: 'Historial',
      discount: 'Descuento', free_hour: 'Hora Gratis', points_per_euro: 'puntos por euro gastado',
      min_points: 'Mínimo', confirm_redeem: 'Confirmar Canje', cancel: 'Cancelar',
      success: '¡Puntos canjeados con éxito!', insufficient: 'Puntos insuficientes',
      earned_from_purchase: 'Ganados por compra', earned_from_reservation: 'Ganados por reserva',
      redeemed_discount: 'Canjeado por descuento', redeemed_hour: 'Canjeado por hora gratis',
      level: 'Nivel', bronze: 'Bronce', silver: 'Plata', gold: 'Oro', platinum: 'Platino',
      next_level: 'Próximo nivel', bonus_multiplier: 'Multiplicador de bonus'
    },
    common: { loading: 'Cargando...', error: 'Error', success: 'Éxito', close: 'Cerrar', back: 'Volver', view_all: 'Ver todo', per_hour: '/hora', available_copies: 'copias disponibles', optional: 'opcional', by: 'Por', light_mode: 'Modo claro', dark_mode: 'Modo oscuro' },
    footer: {
      tagline: 'Tu tienda de juegos de mesa y TCG. Compra, reserva, juega.',
      hours: 'Lun-Dom: 10:00 - 22:00',
      store: 'Tienda',
      board_games: 'Juegos de Mesa',
      accessories: 'Accesorios',
      community: 'Comunidad',
      reserve_game: 'Reservar Juego',
      reserve_table: 'Reservar Mesa',
      rights: 'Todos los derechos reservados.',
    },
    catalog: {
      cards_found: 'cartas encontradas',
      no_cards_found: 'No se encontraron cartas',
      loading_more: 'Cargando más...',
      no_more_cards: 'No hay más cartas',
      search_cards_placeholder: 'Buscar cartas...',
      search: 'Buscar',
      filters: 'Filtros',
      clear_filters: 'Limpiar filtros',
      set_expansion: 'Set / Expansión',
      all_sets: 'Todos los sets',
      rarity: 'Rareza',
      all_rarities: 'Todas las rarezas',
      type: 'Tipo',
      all_types: 'Todos los tipos',
      view_on_riftscribe: 'Ver en RiftScribe',
      cardmarket_prices: 'Ver precios en Cardmarket',
      set_label: 'Set:',
      rarity_label: 'Rareza:',
      where_to_get_card: '¿Dónde conseguir esta carta?',
      finding_products: 'Buscando productos...',
      expansion: 'Expansión',
      cards: 'cartas',
      view_set: 'Ver set',
      available_in_store: 'Productos disponibles en nuestra tienda:',
      view: 'Ver',
      no_expansion_products: 'No hay productos vinculados a esta expansión en nuestra tienda todavía.',
      expansion_not_registered: 'Esta expansión no está registrada en nuestra tienda.',
      view_tcg_store: 'Ver Tienda TCG',
    },
    reservationsShared: {
      availability: 'Disponibilidad',
      total_capacity: 'capacidad total: {count} personas',
      click_to_select_time: 'clic para seleccionar hora',
      full: 'lleno',
      partial: 'Parcial',
      complete: 'Completo',
      your_slot: 'Tu hora',
      select_date_first: 'Selecciona una fecha primero.',
      select_tables: 'Selecciona Mesa(s)',
      selected: 'Seleccionada',
      optional_notes_example: 'Ej: traemos nuestro propio juego',
      people: 'personas',
      total_capacity_label: 'Capacidad total:',
      select_more_tables: 'Selecciona más mesas',
      summary: 'Resumen',
      tables: 'Mesas',
      date: 'Fecha',
      capacity: 'Capacidad',
      linked_game_reservation: 'Vinculada a reserva de juego',
    },
    collectionsShared: {
      all: 'Todos',
      mixed: 'Mixta',
      search_collection_or_user: 'Buscar colección o usuario...',
      sort_name_asc: 'Nombre A-Z',
      sort_name_desc: 'Nombre Z-A',
      sort_cards_desc: 'Más cartas',
      sort_cards_asc: 'Menos cartas',
      collections_found: 'colecciones encontradas',
      login_to_view: 'Inicia sesión para ver tus colecciones',
      no_collections: 'Sin colecciones',
      collection_name_placeholder: 'Nombre de la colección',
      collection_description_placeholder: 'Descripción (opcional)',
      public_collection_help: 'Colección pública (visible para todos)',
      create_collection: 'Crear Colección',
      select_collection: 'Selecciona una colección para ver las cartas.',
      no_cards_in_collection: 'No hay cartas en esta colección.',
      search_card_placeholder: 'Buscar carta...',
      multi_select_hint: 'Haz clic en varias cartas para seleccionarlas y añadirlas a la vez.',
      no_results: 'Sin resultados',
      card_selected_singular: 'carta seleccionada',
      card_selected_plural: 'cartas seleccionadas',
      clear: 'Limpiar',
      add_count: 'Añadir {count}',
      in_collection: 'En colección',
      available_in_store: 'Disponible en la tienda',
      searching: 'Buscando...',
      set_without_products: 'El set existe pero no tiene productos vinculados en la tienda.',
      expansion_not_available: 'Esta expansión no está disponible en la tienda.',
    },
    recommendations: {
      title: 'Recomendados para Ti',
      based_on_reservations: 'Basado en tus reservas anteriores',
      most_popular: 'Los mas populares',
      rent: 'Alquiler',
      add: 'Anadir',
    },
  },
  en: {
    nav: { home: 'Home', store: 'Store', reservations: 'Reservations', events: 'Events', tournaments: 'Tournaments', ranking: 'Leaderboard', collections: 'Collections', profile: 'Profile', admin: 'Control Panel', login: 'Login', register: 'Register', logout: 'Logout', cart: 'Cart', cards: 'Cards' },
    home: {
      hero_title: 'Your Gaming Corner', hero_subtitle: 'Shop, tournaments and reservations for board game and TCG lovers all in one place.',
      hero_cta: 'Visit Store', hero_cta2: 'Book a Table', featured: 'Featured Products', events_title: 'Upcoming Events',
      see_all: 'See All', add_to_cart: 'Add', join_title: 'Join the Community!',
      join_sub: 'Sign up and access reservations, tournaments, collections and much more.',
      join_cta: 'Create Account', feature_store: 'Online Store', feature_store_desc: 'Board games and TCGs at the best prices.',
      feature_reserve: 'Game Rentals', feature_reserve_desc: 'Try our games in store before you buy.',
      feature_table: 'Table Booking', feature_table_desc: 'Bring your own games and book a table.',
      feature_events: 'Tournaments', feature_events_desc: 'Compete in Pokémon, Magic, and more.',
    },
    store: {
      title: 'Store', all: 'All', board_games: 'Board Games', tcg: 'TCG', accessories: 'Accessories',
      search_placeholder: 'Search products...', price_filter: 'Price', sort: 'Sort',
      add_to_cart: 'Add to Cart', rent: 'Reserve', out_of_stock: 'Out of stock',
      players: 'players', minutes: 'min', no_products: 'No products found'
    },
    reservations: {
      game_title: 'Board Game Reservations', table_title: 'Table Reservations',
      select_game: 'Select a Game', select_date: 'Select Date', select_slot: 'Select Time Slot',
      available: 'Available', booked: 'Booked', confirm: 'Confirm Reservation',
      my_reservations: 'My Reservations', cancel: 'Cancel', cancelled: 'Cancelled', confirmed: 'Confirmed',
      duration: 'Duration', party_size: 'Party Size', notes: 'Notes', table: 'Table', live: 'Live', offline: 'Offline',
    },
    events: {
      title: 'Events', tournaments_title: 'Tournaments', register: 'Register', unregister: 'Unregister',
      registered: 'Registered', free: 'Free', participants: 'participants', full: 'Full',
      prize: 'Prize', entry_fee: 'Entry Fee', date: 'Date', time: 'Time',
      tournament: 'Tournament', casual: 'Casual', draft: 'Draft', sealed: 'Sealed',
      events_subtitle: 'Events, game nights, and activities.',
      tournaments_subtitle: 'Tournaments, brackets, and competitions.',
      empty_events: 'No upcoming events.',
      empty_tournaments: 'No upcoming tournaments.',
    },
    collections: {
      title: 'Card Collections', my_collections: 'My Collections', public_collections: 'Public Collections',
      create: 'New Collection', add_card: 'Add Card', search_cards: 'Search Cards',
      pokemon: 'Pokémon', magic: 'Magic', private: 'Private', public: 'Public',
      cards: 'cards', edit: 'Edit', delete: 'Delete', save: 'Save', cancel: 'Cancel',
      share: 'Share', copy_link: 'Copy Link', link_copied: 'Link copied!'
    },
    wishlist: {
      title: 'Wishlist', add: 'Add to Wishlist', remove: 'Remove from Wishlist', empty: 'Your wishlist is empty',
      added: 'Added to wishlist!', removed: 'Removed from wishlist', view: 'View Wishlist'
    },
    product_reserve: {
      title: 'Reserve Product', reserve: 'Reserve', cancel: 'Cancel Reservation',
      reserved: 'Reserved', out_of_stock: 'Out of Stock - Reserve', preorder: 'Pre-order',
      my_reservations: 'My Product Reservations', status_pending: 'Pending', status_confirmed: 'Confirmed',
      success: 'Product reserved! We\'ll notify you when available.',
      people_reserved: 'people have reserved this product'
    },
    cart: {
      title: 'Cart', empty: 'Your cart is empty', checkout: 'Pay with Stripe',
      total: 'Total', remove: 'Remove', continue_shopping: 'Continue Shopping',
      login_required: 'Please log in to checkout'
    },
    auth: {
      login: 'Log In', register: 'Register', email: 'Email',
      password: 'Password', name: 'Full Name', google_login: 'Continue with Google',
      or: 'or', forgot_password: 'Forgot password?', no_account: "Don't have an account?", has_account: 'Already have an account?'
    },
    admin: {
      dashboard: 'Dashboard', users: 'Users', products: 'Products', events: 'Events',
      reservations: 'Reservations', revenue: 'Revenue', new_product: 'New Product', new_event: 'New Event',
    },
    profile: { title: 'My Profile', edit: 'Edit Profile', orders: 'My Orders', reservations: 'My Reservations', save: 'Save', cancel: 'Cancel' },
    loyalty: {
      title: 'Loyalty Points', points: 'points', your_points: 'Your Points', earn_points: 'Earn Points',
      redeem: 'Redeem', redeem_points: 'Redeem Points', history: 'History',
      discount: 'Discount', free_hour: 'Free Hour', points_per_euro: 'points per euro spent',
      min_points: 'Minimum', confirm_redeem: 'Confirm Redemption', cancel: 'Cancel',
      success: 'Points redeemed successfully!', insufficient: 'Insufficient points',
      earned_from_purchase: 'Earned from purchase', earned_from_reservation: 'Earned from reservation',
      redeemed_discount: 'Redeemed for discount', redeemed_hour: 'Redeemed for free hour',
      level: 'Level', bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum',
      next_level: 'Next level', bonus_multiplier: 'Bonus multiplier'
    },
    common: { loading: 'Loading...', error: 'Error', success: 'Success', close: 'Close', back: 'Back', view_all: 'View all', per_hour: '/hour', available_copies: 'copies available', optional: 'optional', by: 'By', light_mode: 'Light mode', dark_mode: 'Dark mode' },
    footer: {
      tagline: 'Your board game and TCG store. Shop, reserve, play.',
      hours: 'Mon-Sun: 10:00 - 22:00',
      store: 'Store',
      board_games: 'Board Games',
      accessories: 'Accessories',
      community: 'Community',
      reserve_game: 'Reserve Game',
      reserve_table: 'Book Table',
      rights: 'All rights reserved.',
    },
    catalog: {
      cards_found: 'cards found',
      no_cards_found: 'No cards found',
      loading_more: 'Loading more...',
      no_more_cards: 'No more cards',
      search_cards_placeholder: 'Search cards...',
      search: 'Search',
      filters: 'Filters',
      clear_filters: 'Clear filters',
      set_expansion: 'Set / Expansion',
      all_sets: 'All sets',
      rarity: 'Rarity',
      all_rarities: 'All rarities',
      type: 'Type',
      all_types: 'All types',
      view_on_riftscribe: 'View on RiftScribe',
      cardmarket_prices: 'Check prices on Cardmarket',
      set_label: 'Set:',
      rarity_label: 'Rarity:',
      where_to_get_card: 'Where to get this card?',
      finding_products: 'Finding products...',
      expansion: 'Expansion',
      cards: 'cards',
      view_set: 'View set',
      available_in_store: 'Available in our store:',
      view: 'View',
      no_expansion_products: 'No products linked to this expansion in our store yet.',
      expansion_not_registered: 'This expansion is not registered in our store.',
      view_tcg_store: 'View TCG Store',
    },
    reservationsShared: {
      availability: 'Availability',
      total_capacity: 'total capacity: {count} people',
      click_to_select_time: 'click to select time',
      full: 'full',
      partial: 'Partial',
      complete: 'Full',
      your_slot: 'Your slot',
      select_date_first: 'Select a date first.',
      select_tables: 'Select Table(s)',
      selected: 'Selected',
      optional_notes_example: 'E.g. bringing our own game',
      people: 'people',
      total_capacity_label: 'Total capacity:',
      select_more_tables: 'Select more tables',
      summary: 'Summary',
      tables: 'Tables',
      date: 'Date',
      capacity: 'Capacity',
      linked_game_reservation: 'Linked to game reservation',
    },
    collectionsShared: {
      all: 'All',
      mixed: 'Mixed',
      search_collection_or_user: 'Search collection or user...',
      sort_name_asc: 'Name A-Z',
      sort_name_desc: 'Name Z-A',
      sort_cards_desc: 'Most cards',
      sort_cards_asc: 'Fewest cards',
      collections_found: 'collections found',
      login_to_view: 'Log in to view your collections',
      no_collections: 'No collections',
      collection_name_placeholder: 'Collection name',
      collection_description_placeholder: 'Description (optional)',
      public_collection_help: 'Public collection (visible to everyone)',
      create_collection: 'Create Collection',
      select_collection: 'Select a collection to view cards.',
      no_cards_in_collection: 'No cards in this collection.',
      search_card_placeholder: 'Search card...',
      multi_select_hint: 'Click multiple cards to select them and add at once.',
      no_results: 'No results',
      card_selected_singular: 'card selected',
      card_selected_plural: 'cards selected',
      clear: 'Clear',
      add_count: 'Add {count}',
      in_collection: 'In collection',
      available_in_store: 'Available in store',
      searching: 'Searching...',
      set_without_products: 'Set found but no linked products in store.',
      expansion_not_available: 'This expansion is not available in the store.',
    },
    recommendations: {
      title: 'Recommended for You',
      based_on_reservations: 'Based on your past reservations',
      most_popular: 'Most popular',
      rent: 'Rent',
      add: 'Add',
    },
  },
  fr: {
    nav: { home: 'Accueil', store: 'Boutique', reservations: 'Réservations', events: 'Événements', tournaments: 'Tournois', ranking: 'Classement', collections: 'Collections', profile: 'Profil', admin: 'Panneau de contrôle', login: 'Connexion', register: "S'inscrire", logout: 'Déconnexion', cart: 'Panier' },
    home: {
      hero_title: 'Votre Coin de Jeux', hero_subtitle: 'Boutique, tournois et réservations pour les amateurs de jeux de société et TCG en un seul endroit.',
      hero_cta: 'Voir la Boutique', hero_cta2: 'Réserver une Table', featured: 'Produits en Vedette', events_title: 'Prochains Événements',
      see_all: 'Voir Tout', add_to_cart: 'Ajouter', join_title: 'Rejoignez la Communauté!',
      join_sub: 'Inscrivez-vous et accédez aux réservations, tournois, collections et bien plus encore.',
      join_cta: 'Créer un Compte', feature_store: 'Boutique en Ligne', feature_store_desc: 'Jeux de société et TCG aux meilleurs prix.',
      feature_reserve: 'Location de Jeux', feature_reserve_desc: 'Essayez nos jeux en magasin avant d\'acheter.',
      feature_table: 'Réservation de Tables', feature_table_desc: 'Apportez vos propres jeux et réservez une table.',
      feature_events: 'Tournois et Événements', feature_events_desc: 'Participez aux tournois Pokémon, Magic et plus.',
    },
    store: {
      title: 'Boutique', all: 'Tout', board_games: 'Jeux de Société', tcg: 'TCG', accessories: 'Accessoires',
      search_placeholder: 'Rechercher...', price_filter: 'Prix', sort: 'Trier',
      add_to_cart: 'Ajouter au Panier', rent: 'Réserver', out_of_stock: 'Épuisé',
      players: 'joueurs', minutes: 'min', no_products: 'Aucun produit trouvé'
    },
    reservations: {
      game_title: 'Réservation de Jeux de Société', table_title: 'Réservation de Tables',
      select_game: 'Sélectionner un Jeu', select_date: 'Sélectionner la Date', select_slot: 'Sélectionner le Créneau',
      available: 'Disponible', booked: 'Réservé', confirm: 'Confirmer la Réservation',
      my_reservations: 'Mes Réservations', cancel: 'Annuler', cancelled: 'Annulée', confirmed: 'Confirmée',
      duration: 'Durée', party_size: 'Groupe', notes: 'Notes', table: 'Table', live: 'En direct', offline: 'Hors ligne',
    },
    events: {
      title: 'Événements', tournaments_title: 'Tournois', register: "S'inscrire", unregister: 'Se désinscrire',
      registered: 'Inscrit', free: 'Gratuit', participants: 'participants', full: 'Complet',
      prize: 'Prix', entry_fee: 'Inscription', date: 'Date', time: 'Heure',
      tournament: 'Tournoi', casual: 'Casual', draft: 'Draft', sealed: 'Scellé',
      events_subtitle: 'Événements, soirées jeux et activités.',
      tournaments_subtitle: 'Tournois, brackets et compétitions.',
      empty_events: 'Aucun événement à venir.',
      empty_tournaments: 'Aucun tournoi à venir.',
    },
    collections: {
      title: 'Collections de Cartes', my_collections: 'Mes Collections', public_collections: 'Collections Publiques',
      create: 'Nouvelle Collection', add_card: 'Ajouter une Carte', search_cards: 'Rechercher des Cartes',
      pokemon: 'Pokémon', magic: 'Magic', private: 'Privée', public: 'Publique',
      cards: 'cartes', edit: 'Modifier', delete: 'Supprimer', save: 'Enregistrer', cancel: 'Annuler',
    },
    cart: {
      title: 'Panier', empty: 'Votre panier est vide', checkout: 'Payer avec Stripe',
      total: 'Total', remove: 'Supprimer', continue_shopping: 'Continuer vos Achats',
      login_required: 'Connectez-vous pour acheter'
    },
    auth: {
      login: 'Se Connecter', register: "S'inscrire", email: 'Email',
      password: 'Mot de passe', name: 'Nom complet', google_login: 'Continuer avec Google',
      or: 'ou', forgot_password: 'Mot de passe oublié?', no_account: "Pas encore de compte?", has_account: 'Déjà un compte?'
    },
    admin: {
      dashboard: 'Tableau de Bord', users: 'Utilisateurs', products: 'Produits', events: 'Événements',
      reservations: 'Réservations', revenue: 'Revenus', new_product: 'Nouveau Produit', new_event: 'Nouvel Événement',
    },
    profile: { title: 'Mon Profil', edit: 'Modifier le Profil', orders: 'Mes Commandes', reservations: 'Mes Réservations', save: 'Enregistrer', cancel: 'Annuler' },
    loyalty: {
      title: 'Points de Fidélité', points: 'points', your_points: 'Vos Points', earn_points: 'Gagner des Points',
      redeem: 'Échanger', redeem_points: 'Échanger des Points', history: 'Historique',
      discount: 'Réduction', free_hour: 'Heure Gratuite', points_per_euro: 'points par euro dépensé',
      min_points: 'Minimum', confirm_redeem: "Confirmer l'Échange", cancel: 'Annuler',
      success: 'Points échangés avec succès!', insufficient: 'Points insuffisants',
      earned_from_purchase: "Gagnés par l'achat", earned_from_reservation: 'Gagnés par réservation',
      redeemed_discount: 'Échangé pour une réduction', redeemed_hour: 'Échangé pour heure gratuite',
      level: 'Niveau', bronze: 'Bronze', silver: 'Argent', gold: 'Or', platinum: 'Platine',
      next_level: 'Niveau suivant', bonus_multiplier: 'Multiplicateur de bonus'
    },
    common: { loading: 'Chargement...', error: 'Erreur', success: 'Succès', close: 'Fermer', back: 'Retour', view_all: 'Voir tout', per_hour: '/heure', available_copies: 'exemplaires disponibles' }
  },
  pt: {
    nav: { home: 'Início', store: 'Loja', reservations: 'Reservas', events: 'Eventos', tournaments: 'Torneios', ranking: 'Ranking', collections: 'Coleções', profile: 'Perfil', admin: 'Painel de controlo', login: 'Entrar', register: 'Registrar', logout: 'Sair', cart: 'Carrinho' },
    home: {
      hero_title: 'Seu Cantinho de Jogos', hero_subtitle: 'Loja, torneios e reservas para amantes de jogos de mesa e TCG em um só lugar.',
      hero_cta: 'Ver Loja', hero_cta2: 'Reservar Mesa', featured: 'Produtos em Destaque', events_title: 'Próximos Eventos',
      see_all: 'Ver Todos', add_to_cart: 'Adicionar', join_title: 'Junte-se à Comunidade!',
      join_sub: 'Cadastre-se e acesse reservas, torneios, coleções e muito mais.',
      join_cta: 'Criar Conta', feature_store: 'Loja Online', feature_store_desc: 'Jogos de mesa e TCG com os melhores preços.',
      feature_reserve: 'Aluguel de Jogos', feature_reserve_desc: 'Experimente nossos jogos na loja antes de comprar.',
      feature_table: 'Reserva de Mesas', feature_table_desc: 'Traga seus próprios jogos e reserve uma mesa.',
      feature_events: 'Torneios e Eventos', feature_events_desc: 'Participe de torneios de Pokémon, Magic e mais.',
    },
    store: {
      title: 'Loja', all: 'Tudo', board_games: 'Jogos de Mesa', tcg: 'TCG', accessories: 'Acessórios',
      search_placeholder: 'Buscar produtos...', price_filter: 'Preço', sort: 'Ordenar',
      add_to_cart: 'Adicionar ao Carrinho', rent: 'Reservar', out_of_stock: 'Sem estoque',
      players: 'jogadores', minutes: 'min', no_products: 'Nenhum produto encontrado'
    },
    reservations: {
      game_title: 'Reserva de Jogos de Mesa', table_title: 'Reserva de Mesas',
      select_game: 'Selecionar um Jogo', select_date: 'Selecionar Data', select_slot: 'Selecionar Horário',
      available: 'Disponível', booked: 'Ocupado', confirm: 'Confirmar Reserva',
      my_reservations: 'Minhas Reservas', cancel: 'Cancelar', cancelled: 'Cancelada', confirmed: 'Confirmada',
      duration: 'Duração', party_size: 'Grupo', notes: 'Notas', table: 'Mesa', live: 'Ao vivo', offline: 'Offline',
    },
    events: {
      title: 'Eventos', tournaments_title: 'Torneios', register: 'Inscrever-se', unregister: 'Cancelar Inscrição',
      registered: 'Inscrito', free: 'Grátis', participants: 'participantes', full: 'Completo',
      prize: 'Prêmio', entry_fee: 'Inscrição', date: 'Data', time: 'Hora',
      tournament: 'Torneio', casual: 'Casual', draft: 'Draft', sealed: 'Selado',
      events_subtitle: 'Eventos, noites de jogo e atividades.',
      tournaments_subtitle: 'Torneios, brackets e competições.',
      empty_events: 'Não há eventos próximos.',
      empty_tournaments: 'Não há torneios próximos.',
    },
    collections: {
      title: 'Coleções de Cartas', my_collections: 'Minhas Coleções', public_collections: 'Coleções Públicas',
      create: 'Nova Coleção', add_card: 'Adicionar Carta', search_cards: 'Buscar Cartas',
      pokemon: 'Pokémon', magic: 'Magic', private: 'Privada', public: 'Pública',
      cards: 'cartas', edit: 'Editar', delete: 'Excluir', save: 'Salvar', cancel: 'Cancelar',
    },
    cart: {
      title: 'Carrinho', empty: 'Seu carrinho está vazio', checkout: 'Pagar com Stripe',
      total: 'Total', remove: 'Remover', continue_shopping: 'Continuar Comprando',
      login_required: 'Faça login para comprar'
    },
    auth: {
      login: 'Entrar', register: 'Registrar', email: 'Email',
      password: 'Senha', name: 'Nome completo', google_login: 'Continuar com Google',
      or: 'ou', forgot_password: 'Esqueceu a senha?', no_account: 'Não tem conta?', has_account: 'Já tem conta?'
    },
    admin: {
      dashboard: 'Painel de Controle', users: 'Usuários', products: 'Produtos', events: 'Eventos',
      reservations: 'Reservas', revenue: 'Receita', new_product: 'Novo Produto', new_event: 'Novo Evento',
    },
    profile: { title: 'Meu Perfil', edit: 'Editar Perfil', orders: 'Meus Pedidos', reservations: 'Minhas Reservas', save: 'Salvar', cancel: 'Cancelar' },
    loyalty: {
      title: 'Pontos de Fidelidade', points: 'pontos', your_points: 'Seus Pontos', earn_points: 'Ganhar Pontos',
      redeem: 'Resgatar', redeem_points: 'Resgatar Pontos', history: 'Histórico',
      discount: 'Desconto', free_hour: 'Hora Grátis', points_per_euro: 'pontos por euro gasto',
      min_points: 'Mínimo', confirm_redeem: 'Confirmar Resgate', cancel: 'Cancelar',
      success: 'Pontos resgatados com sucesso!', insufficient: 'Pontos insuficientes',
      earned_from_purchase: 'Ganhos por compra', earned_from_reservation: 'Ganhos por reserva',
      redeemed_discount: 'Resgatado por desconto', redeemed_hour: 'Resgatado por hora grátis',
      level: 'Nível', bronze: 'Bronze', silver: 'Prata', gold: 'Ouro', platinum: 'Platina',
      next_level: 'Próximo nível', bonus_multiplier: 'Multiplicador de bônus'
    },
    common: { loading: 'Carregando...', error: 'Erro', success: 'Sucesso', close: 'Fechar', back: 'Voltar', view_all: 'Ver tudo', per_hour: '/hora', available_copies: 'cópias disponíveis' }
  },
  de: {
    nav: { home: 'Startseite', store: 'Shop', reservations: 'Reservierungen', events: 'Events', tournaments: 'Turniere', ranking: 'Rangliste', collections: 'Sammlungen', profile: 'Profil', admin: 'Kontrollzentrum', login: 'Anmelden', register: 'Registrieren', logout: 'Abmelden', cart: 'Warenkorb' },
    home: {
      hero_title: 'Deine Spielecke', hero_subtitle: 'Shop, Turniere und Reservierungen für Brettspiel- und TCG-Liebhaber an einem Ort.',
      hero_cta: 'Shop Besuchen', hero_cta2: 'Tisch Reservieren', featured: 'Empfohlene Produkte', events_title: 'Kommende Events',
      see_all: 'Alle Ansehen', add_to_cart: 'Hinzufügen', join_title: 'Werde Teil der Community!',
      join_sub: 'Registriere dich und erhalte Zugang zu Reservierungen, Turnieren, Sammlungen und vielem mehr.',
      join_cta: 'Konto Erstellen', feature_store: 'Online Shop', feature_store_desc: 'Brettspiele und TCGs zu den besten Preisen.',
      feature_reserve: 'Spieleverleih', feature_reserve_desc: 'Teste unsere Spiele im Laden vor dem Kauf.',
      feature_table: 'Tischreservierung', feature_table_desc: 'Bring deine eigenen Spiele mit und reserviere einen Tisch.',
      feature_events: 'Turniere & Events', feature_events_desc: 'Nimm an Pokémon, Magic Turnieren und mehr teil.',
    },
    store: {
      title: 'Shop', all: 'Alle', board_games: 'Brettspiele', tcg: 'TCG', accessories: 'Zubehör',
      search_placeholder: 'Produkte suchen...', price_filter: 'Preis', sort: 'Sortieren',
      add_to_cart: 'In den Warenkorb', rent: 'Reservieren', out_of_stock: 'Ausverkauft',
      players: 'Spieler', minutes: 'Min', no_products: 'Keine Produkte gefunden'
    },
    reservations: {
      game_title: 'Brettspiel-Reservierung', table_title: 'Tischreservierung',
      select_game: 'Spiel Auswählen', select_date: 'Datum Auswählen', select_slot: 'Zeitfenster Auswählen',
      available: 'Verfügbar', booked: 'Gebucht', confirm: 'Reservierung Bestätigen',
      my_reservations: 'Meine Reservierungen', cancel: 'Stornieren', cancelled: 'Storniert', confirmed: 'Bestätigt',
      duration: 'Dauer', party_size: 'Gruppengröße', notes: 'Notizen', table: 'Tisch', live: 'Live', offline: 'Offline',
    },
    events: {
      title: 'Events', tournaments_title: 'Turniere', register: 'Anmelden', unregister: 'Abmelden',
      registered: 'Angemeldet', free: 'Kostenlos', participants: 'Teilnehmer', full: 'Ausgebucht',
      prize: 'Preis', entry_fee: 'Teilnahmegebühr', date: 'Datum', time: 'Uhrzeit',
      tournament: 'Turnier', casual: 'Casual', draft: 'Draft', sealed: 'Sealed',
      events_subtitle: 'Events, Spieleabende und Aktivitäten.',
      tournaments_subtitle: 'Turniere, Brackets und Wettbewerbe.',
      empty_events: 'Keine kommenden Events.',
      empty_tournaments: 'Keine kommenden Turniere.',
    },
    collections: {
      title: 'Kartensammlungen', my_collections: 'Meine Sammlungen', public_collections: 'Öffentliche Sammlungen',
      create: 'Neue Sammlung', add_card: 'Karte Hinzufügen', search_cards: 'Karten Suchen',
      pokemon: 'Pokémon', magic: 'Magic', private: 'Privat', public: 'Öffentlich',
      cards: 'Karten', edit: 'Bearbeiten', delete: 'Löschen', save: 'Speichern', cancel: 'Abbrechen',
    },
    cart: {
      title: 'Warenkorb', empty: 'Dein Warenkorb ist leer', checkout: 'Mit Stripe Bezahlen',
      total: 'Gesamt', remove: 'Entfernen', continue_shopping: 'Weiter Einkaufen',
      login_required: 'Bitte anmelden zum Bezahlen'
    },
    auth: {
      login: 'Anmelden', register: 'Registrieren', email: 'E-Mail',
      password: 'Passwort', name: 'Vollständiger Name', google_login: 'Mit Google Fortfahren',
      or: 'oder', forgot_password: 'Passwort vergessen?', no_account: 'Noch kein Konto?', has_account: 'Schon ein Konto?'
    },
    admin: {
      dashboard: 'Dashboard', users: 'Benutzer', products: 'Produkte', events: 'Events',
      reservations: 'Reservierungen', revenue: 'Einnahmen', new_product: 'Neues Produkt', new_event: 'Neuer Event',
    },
    profile: { title: 'Mein Profil', edit: 'Profil Bearbeiten', orders: 'Meine Bestellungen', reservations: 'Meine Reservierungen', save: 'Speichern', cancel: 'Abbrechen' },
    loyalty: {
      title: 'Treuepunkte', points: 'Punkte', your_points: 'Deine Punkte', earn_points: 'Punkte Sammeln',
      redeem: 'Einlösen', redeem_points: 'Punkte Einlösen', history: 'Verlauf',
      discount: 'Rabatt', free_hour: 'Gratis Stunde', points_per_euro: 'Punkte pro Euro',
      min_points: 'Minimum', confirm_redeem: 'Einlösung Bestätigen', cancel: 'Abbrechen',
      success: 'Punkte erfolgreich eingelöst!', insufficient: 'Nicht genug Punkte',
      earned_from_purchase: 'Durch Kauf verdient', earned_from_reservation: 'Durch Reservierung verdient',
      redeemed_discount: 'Für Rabatt eingelöst', redeemed_hour: 'Für Gratisstunde eingelöst',
      level: 'Stufe', bronze: 'Bronze', silver: 'Silber', gold: 'Gold', platinum: 'Platin',
      next_level: 'Nächste Stufe', bonus_multiplier: 'Bonus-Multiplikator'
    },
    common: { loading: 'Laden...', error: 'Fehler', success: 'Erfolg', close: 'Schließen', back: 'Zurück', view_all: 'Alle ansehen', per_hour: '/Stunde', available_copies: 'Exemplare verfügbar' }
  }
};

const mergeDeep = (target = {}, source = {}) => {
  const output = { ...target };
  Object.entries(source || {}).forEach(([key, value]) => {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      target[key] &&
      typeof target[key] === 'object' &&
      !Array.isArray(target[key])
    ) {
      output[key] = mergeDeep(target[key], value);
    } else {
      output[key] = value;
    }
  });
  return output;
};

const mergeAdminTranslations = (lang) => mergeDeep(
  {
    ...baseTranslations[lang],
    admin: {
      ...baseTranslations[lang]?.admin,
      controlPanel: CONTROL_PANEL_TRANSLATIONS[lang] || CONTROL_PANEL_TRANSLATIONS.en,
    },
  },
  EXTRA_TRANSLATIONS[lang] || {},
);

const translations = {
  es: mergeAdminTranslations('es'),
  en: mergeAdminTranslations('en'),
  fr: mergeAdminTranslations('fr'),
  pt: mergeAdminTranslations('pt'),
  de: mergeAdminTranslations('de'),
};

// Available languages with their display names and flags
export const LANGUAGES = [
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('camo_lang') || 'es');
  const locale = useMemo(() => getLocaleFromLang(lang), [lang]);
  
  const switchLang = useCallback((l) => { 
    setLang(l); 
    localStorage.setItem('camo_lang', l); 
  }, []);
  
  const t = useCallback((path) => {
    const keys = path.split('.');
    let val = translations[lang] || translations.en;
    for (const key of keys) {
      val = val?.[key];
    }
    if (val !== undefined && val !== null) return val;

    let fallbackVal = translations.en;
    for (const key of keys) {
      fallbackVal = fallbackVal?.[key];
    }
    return fallbackVal || path;
  }, [lang]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    lang, locale, switchLang, t, languages: LANGUAGES
  }), [lang, locale, switchLang, t]);

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
}

export const useLang = () => useContext(LanguageContext);
